import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { connect, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { isEmpty } from 'lodash'

import ScanTicketGif from '../assets/gifs/ticket.gif'

import styles from './ScanScreen.module.scss'
import GratuityHeader from '../components/GratuityHeader'
import SegmentedControlV2 from '../components/segmentedControl/SegmentedControl'
import { KEY_CODES } from '../kiosk/utils/constants'
import useCreateOrderInProgress from '../hooks/useCreateOrderInProgress'
import useGetTicketBalance from '../hooks/useGetTicketBalance'
import { getOrder, getOrderInProgress } from '../selectors/order'
import ConnectingIndicator from '../components/ConnectingIndicator'
import { centsToDollars } from '../utils/formatters'
import { addPaymentToOrderInProgress, clearOrderInProgress, closeNoOpTender, setOrderInProgress } from '../actions/order'
import { toast } from 'react-toastify'
import PeripheralBridge from '../utils/peripheralBridge'
import VNConcessionsNavBar from '../components/VNConcessionsNavBar'
import { handleFullPpiFlow } from '../VNPPI/Utils'
import { VNPPI_TENDERS } from '../VNPPI/Enums'
import { MobileScanner } from '../VNMobileScanner/components/MobileScanner'
import { CameraAlt } from '@material-ui/icons'
import ConfirmModalV2 from '../components/ConfirmModalV2'
import { getDeviceMode } from '../VNMode/Selectors'
import { MODES } from '../VNMode/Reducer'
import { sendCFDScreenNav } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import { getVNScannerInitialized, getVNScannerResults } from '../VNMobileScanner/Selectors'
import { setCurrentPaymentFlow } from '../VNCustomerFacingDisplay/ActionCreators'
import { CFD_POS_PAYMENT_FLOWS } from '../VNCustomerFacingDisplay/Enums'
import { getCurrentPaymentFlow } from '../VNCustomerFacingDisplay/Selectors'
import { VNInjectablePpi } from '../VNPPI/containers/VNInjectablePpi'
import { codeAlreadyUsed } from '../VNUtil/VNUtils'
import { isTabbed } from '../utils/orderStates'
import { ToastManager } from '../utils/toastManager'

const ticketTenderChoices = [
  { label: 'Scan Ticket', value: 'scan' },
  { label: 'Type Ticket', value: 'entry' },
]

const TicketTender = ({
  className,
  onGoBack,
  needCreateOrder,
  order,
  addPaymentToOrderInProgress,
  balanceDueInCents,
  amountInCents,
  placeFreeOrder,
  push,
  tabOrderInState,
}) => {
  const classNames = cn(styles.scanScreen, className)
  const isMobile = window.matchMedia('(max-width: 700px)')?.matches
  const [selectedSubmissionType, setSelectedSubmissionType] = useState(isMobile ? 'entry' : 'scan')
  const [isProcessing, setIsProcessing] = useState(false)
  const [ticketId, setTicketId] = useState('')
  const [createOrder, orderCreated] = useCreateOrderInProgress()
  const mobileScanResults = useSelector(state => getVNScannerResults(state))
  const [getTicketBalance, { ticketBalance, directAuthEnabled, dataSucceeded, dataFailed }] = useGetTicketBalance()
  const [componentMounted, setComponentMounted] = useState(false)
  const [keyboardIsActive, setKeyboardIsActive] = useState(false)
  const [codeError, setCodeError] = useState(false)
  const [mobileScannerEnabled, setMobileScannerEnabled] = useState(true)
  const [isMobilePaymentSelection, setIsMobilePaymentSelection] = useState(false)
  const [mobileProcessingText, setMobileProcessingText] = useState('Please scan ticket')
  const [scannerLive, setScannerLive] = useState(false)

  const isMobileScannerInitialized = useSelector(state => getVNScannerInitialized(state))
  const currentPaymentFlow = useSelector(state => getCurrentPaymentFlow(state))
  const cfdMode = useSelector(state => getDeviceMode(state))
  const ticketInputRef = useRef()
  const orderStillHasRemainingBalance = balanceDueInCents && balanceDueInCents !== amountInCents

  const ppiInjectableRef = useRef()

  const stopScanner = () => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance) return
    if (!scannerLive) return

    setScannerLive(false)
    bridgeInstance.call('stopScanner')
  }

  const resetTicket = () => {
    setTicketId('')
    setIsProcessing(false)
  }

  const closeKeyBoard = () => {
    if (keyboardIsActive) {
      ticketInputRef?.current?.blur()
    }
  }

  const handleFocusChange = () => {
    setTimeout(() => {
      setKeyboardIsActive(document.activeElement === ticketInputRef?.current)
    }, 150)
  }

  useEffect(() => {
    if (needCreateOrder) {
      createOrder()
    }
    setComponentMounted(true)

    document.addEventListener('focusin', handleFocusChange)
    document.addEventListener('focusout', handleFocusChange)

    return () => {
      stopScanner()
      document.removeEventListener('focusin', handleFocusChange)
      document.removeEventListener('focusout', handleFocusChange)
    }
  }, [])

  useEffect(() => {
    if (dataFailed) {
      setIsProcessing(false)
      setCodeError(true)
    }
  }, [dataFailed])

  useEffect(() => {
    if (orderCreated && order?.amountInCents === 0) {
      placeFreeOrder()
    }
  }, [orderCreated])

  const startBalanceCheck = async (ticketToCheck) => {

    setIsProcessing(true)
    setCodeError(false)

    const ppiInjectableData = ppiInjectableRef.current.getData(VNPPI_TENDERS.TICKET_TENDER)

    if (!isEmpty(ppiInjectableData.ppiTender)) {

      const externalFunctionality = {
        functions: {
          ...ppiInjectableData.functions,
          setError: setCodeError,
          placeFreeOrder: placeFreeOrder
        },
        balanceDueInCents: ppiInjectableData.order?.balanceDueInCents,
        promotions: ppiInjectableData.promotions,
        token: ticketToCheck,
        paymentType: "vnapi_ticket",
        shortDescription: "Ticket"
      }

      await handleFullPpiFlow(ppiInjectableData, externalFunctionality)

      resetTicket()

    } else {
      getTicketBalance(ticketToCheck)
    }
  }

  useEffect(() => {
    if (mobileScanResults && mobileScanResults?.get('results')) {
      onMobileScan(mobileScanResults?.get("results"))
    }
  }, [mobileScanResults])

  useEffect(() => {
    // current payment flow is needed because the screen is being sent over while the component is unmounting.
    if (cfdMode !== MODES.POS || currentPaymentFlow !== CFD_POS_PAYMENT_FLOWS.TICKET || balanceDueInCents <= 0) {
      return
    }

    if (balanceDueInCents !== amountInCents) {
      sendCFDScreenNav(CFD_SCREEN.TICKET_PAYMENT_BALANCE, { balanceToBePaid: { amountPaid: (amountInCents - balanceDueInCents), balanceDue: balanceDueInCents}})
    } else {
      sendCFDScreenNav(CFD_SCREEN.TICKET_PAYMENT)
    }
  }, [balanceDueInCents])

  useEffect(() => {
    if (dataSucceeded) {
      setCodeError(false)
      let tenderAmountInCents
      let remainingTicketBalance
      if (directAuthEnabled) {
        tenderAmountInCents = balanceDueInCents
      } else
      if (+ticketBalance === 0) {
        resetTicket()
        setCodeError(true)
        ToastManager.error('No funds remain on this ticket. Please scan another ticket.')
        return
      } else
      if (ticketBalance >= balanceDueInCents) {
        tenderAmountInCents = balanceDueInCents
        remainingTicketBalance = ticketBalance - balanceDueInCents
      } else {
        tenderAmountInCents = ticketBalance
        remainingTicketBalance = 0
      }
      addPaymentToOrderInProgress({
        tenderAmountInCents,
        paymentType: 'loaded_ticket',
        shortDescription: 'Ticket',
        token: ticketId,
        remainingTicketBalance,
        directAuthEnabled,
      })
      resetTicket()
    }
  }, [dataSucceeded])

  useEffect(() => {
    if (needCreateOrder || !componentMounted) return

    if (!balanceDueInCents) {
      setIsProcessing(true)
    } else if (orderStillHasRemainingBalance) {
      ToastManager.success(
        `Ticket ${selectedSubmissionType} successful. Please ${
          selectedSubmissionType === 'scan' ? 'scan' : 'input'
        } another ticket to pay remaining balance`
      )
      if (isMobile && navigator?.userAgent?.includes("A800")) {
        setIsMobilePaymentSelection(true)
      }
      if (selectedSubmissionType === 'entry') {
        ticketInputRef.current?.focus()
      }
    }
  }, [balanceDueInCents])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (selectedSubmissionType === 'entry' && ticketInputRef.current) {
      setScannerLive(false)
      ticketInputRef.current?.focus()
    }

    if (selectedSubmissionType === 'scan') {
      if (!bridgeInstance) return
      setScannerLive(true)
      bridgeInstance.registerHandler('scanPayload', function (data, callback) {
        const scannedTicket = data?.valueOf()
        if (codeAlreadyUsed(order, scannedTicket)) {
          ToastManager.error('Ticket already scanned and applied to order')
        } else {
          ToastManager.success(`Ticket Scan Successful`)
          setTicketId(scannedTicket)
          startBalanceCheck(scannedTicket)
        }
      })

      bridgeInstance.registerHandler('scanError', function (err, callback) {
        if (err?.toLowerCase()?.includes('no scanner detected')) {
          setSelectedSubmissionType('entry')
        }
        setCodeError(true)
        ToastManager.error(`Ticket Scan Error, ERROR: ${JSON.stringify(err)}`)
      })
    }
  }, [selectedSubmissionType, order])

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return

    if (isProcessing || selectedSubmissionType === 'entry') {
      stopScanner()
      setScannerLive(false)
    } else if (selectedSubmissionType === 'scan') {
      bridgeInstance.call('startScanner')
      setScannerLive(true)
    }
  }, [isProcessing, selectedSubmissionType])

  useEffect(() => {
    if (isProcessing) {
      setMobileProcessingText('Processing...')
    } else {
      setMobileProcessingText('Please scan ticket')
    }
  }, [isProcessing])

  const primaryText = centsToDollars(balanceDueInCents)
  const secondaryText =
    selectedSubmissionType === 'scan'
      ? 'Please scan ticket QR code or barcode.'
      : 'Please type ticket number'
  const extraText =
    !!orderStillHasRemainingBalance &&
    `Total: ${centsToDollars(
      amountInCents
    )} • Balance Paid: ${centsToDollars(
      amountInCents - balanceDueInCents
    )}`

  const handleSubmissionTypeChange = value => {
    if (selectedSubmissionType !== value) {
      setSelectedSubmissionType(value)
    }
  }

  const handleKeyPress = e => {
    if (e.keyCode === KEY_CODES.ENTER) {
      const isValid = /^[a-z|0-9\-]*?$/gi.test(ticketId);
      if(isValid){
        if (codeAlreadyUsed(order, ticketId)) {
          ToastManager.error('Ticket already entered and applied to order')
        } else {
          closeKeyBoard()

          // Enforce a slight delay to prevent the on screen keyboard from freezing
          // due to device resources being used for network calls and order processing.
          setTimeout(() => {
            startBalanceCheck(ticketId)
          }, 300)
        }
      } else {
        ToastManager.error('The ticket ID contains invalid characters')
      }
    }
  }

  const onMobileCameraClick = () => {
    setMobileScannerEnabled(true)
  }

  const onMobileScan = (scanResult) => {
    if (codeAlreadyUsed(order, scanResult)) {
      ToastManager.error('QR code already scanned and applied to order')
      return
    }

    setTicketId(scanResult)
    startBalanceCheck(scanResult)
  }

  const handleTicketCodeChange = e => {
    setTicketId(e?.target?.value)
  }

  if (!orderCreated && needCreateOrder) {
    return <ConnectingIndicator />
  }

  const tenderOptionButtonText = codeError ? 'Pay With Other Method' : "I Don't Have Another Ticket"
  const showTenderOptionButton =
  !isProcessing &&
  !keyboardIsActive &&
  (orderStillHasRemainingBalance || ticketBalance === 0 || codeError)

  const chooseTenderOptionButton = showTenderOptionButton && (
      <div
        className={styles.chooseTenderOption}
        onClick={() => {
          push({
            pathname: '/tender-selection',
            state: {
              tenderAmountInCents: balanceDueInCents,
              scanScreenType: 'ticket',
              fromSplit: true,
            }
          })
        }
        }
      >
        {tenderOptionButtonText}
      </div>
    )

  const redirect = (tenderAmountInCents) => {
    push({
      pathname: '/tender-selection',
      state: {
        tenderAmountInCents,
        scanScreenType: 'ticket',
        fromSplit: true,
      }
    })
  }

  const mobilePaymentSelection = () => {
    if (isMobilePaymentSelection) {
      return (
        <ConfirmModalV2
          onButtonOneClick={() => {
            if (balanceDueInCents > 0) {
              redirect(balanceDueInCents)
            }
          }}
          onButtonTwoClick={() => {
            setIsMobilePaymentSelection(false)
          }}
          headerText={'Scan Another Ticket?'}
          subtext={'Scan ticket successful. How would you like to pay the remaining balance?'}
          isKiosk={false}
          buttonOneText={'OTHER TENDER'}
          buttonTwoText={'TICKET'}
        />
      )
    }

    return
  }

  const scanError = () => {
    if (codeError) {
      return (
        <ConfirmModalV2
          onButtonOneClick={() => {
            if (balanceDueInCents > 0) {
              redirect(balanceDueInCents)
            }
          }}
          onButtonTwoClick={() => {
            setCodeError(false)
          }}
          headerText={null}
          subtext={'There was an issue when scanning this ticket. Would you like to try again?'}
          isKiosk={false}
          buttonOneText={'OTHER TENDER'}
          buttonTwoText={'TRY AGAIN'}
        />
      )
    }

    return
  }

  const displayVNInjectablePpi = () => {
    return <VNInjectablePpi ref={ppiInjectableRef} />
  }

  const display = () => {
    if (mobileScannerEnabled && isMobileScannerInitialized) {
      return (
        <div className={classNames}>
          {displayVNInjectablePpi()}
          <MobileScanner
            onKeyboardClick={() => {
              setMobileScannerEnabled(false)
              setTimeout(() => {
                ticketInputRef?.current?.focus()
              }, 150);
            }}
            goBack={() => {
              onGoBack(isEmpty(order?.payments), cfdMode === MODES.POS, tabOrderInState, isTabbed(order))
            }}
            textDisplay={primaryText}
            toolTipText={mobileProcessingText}
          />
          {mobilePaymentSelection()}
          {scanError()}
        </div>
      )
    }

    return (
      <div className={classNames} >
        {displayVNInjectablePpi()}
        <VNConcessionsNavBar
          onClick={() => {
            closeKeyBoard()
            setTimeout(() => {
              onGoBack(isEmpty(order?.payments), cfdMode === MODES.POS, tabOrderInState, isTabbed(order))
            }, 200)
          }}
          rightAccessory={
            isMobileScannerInitialized &&
            <CameraAlt onClick={onMobileCameraClick}/>
          }
          textDisplay={isMobile && primaryText}
        />
        <div className={styles.scanScreen__header}>
          <GratuityHeader
            className={styles.gratuityHeader}
            primaryText={isMobile ? '' : primaryText}
            secondaryText={secondaryText}
            extraText={extraText}
          />
        </div>
        {!isMobile && <div className={styles.scanScreen__submissionChoices}>
          <SegmentedControlV2
            className={styles.segmentedControl}
            items={ticketTenderChoices}
            selectedValue={selectedSubmissionType}
            onSelect={handleSubmissionTypeChange}
          />
        </div>}
        <div className={styles.scanScreen__body}>
          <div className={styles.choicesContent}>
            <div
              className={cn(styles.option, {
                [styles.isHidden]:
                  selectedSubmissionType === 'entry' || isProcessing
              })}
            >
              <img src={ScanTicketGif} alt={"Scan Ticket"} className={styles.qrImage}/>
            </div>
            <div
              className={cn(styles.option, {
                [styles.isHidden]:
                  selectedSubmissionType === 'scan' || isProcessing
              })}
            >
              <input
                className={styles.manualEntry}
                ref={ticketInputRef}
                value={ticketId}
                onChange={handleTicketCodeChange}
                onKeyDown={handleKeyPress}
              />
            </div>
            <div
              className={cn(styles.processing, {
                [styles.isHidden]: !isProcessing
              })}
            >
              <div className={styles.loadingSpinner}>
                <ConnectingIndicator position={'initial'}/>
              </div>
              <span className={styles.topText}>Processing...</span>
              <span className={styles.bottomText}>please wait.</span>
            </div>
          </div>
          {chooseTenderOptionButton}
        </div>
      </div>
    )
  }

  return display()
}

const mapStateToProps = (state, props) => {
  const order = getOrderInProgress(state)
  const balanceDueInCents = order?.balanceDueInCents
  const amountInCents = order?.amountInCents
  const push = props?.history?.push ?? (() => {})

  // order would only exist in order list state if being a tabbed order
  const tabOrderInState = getOrder(state, order.uuid)

  return {
    order,
    needCreateOrder: isEmpty(order),
    amountInCents,
    balanceDueInCents,
    push,
    tabOrderInState,
  }
}

const mapDispatchToProps = (dispatch, props) => {
  const goBack = props?.history?.goBack ?? (() => {})

  return {
    onGoBack: (noPaymentsOnOrder, isCfdCompanionPos, tabOrderInState, isOrderTabbed) => {
      if (isCfdCompanionPos) {
        dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
        if (isOrderTabbed) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
        } else {
          sendCFDScreenNav(CFD_SCREEN.CART)
        }
      }
      if (!isEmpty(tabOrderInState)) {
        dispatch(setOrderInProgress(tabOrderInState))
      } else
      if (noPaymentsOnOrder || isCfdCompanionPos) {
        dispatch(clearOrderInProgress())
      }
      goBack()
    },
    addPaymentToOrderInProgress: payment =>
      dispatch(addPaymentToOrderInProgress(payment)),
    placeFreeOrder: (payload) => {
      dispatch(closeNoOpTender(payload))
    },
  }
}
export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TicketTender)
)

